export const IMPRESSION_FORMATTER = new Intl.NumberFormat("en-US");

export const CURRENCY_FORMATTER = new Intl.NumberFormat("en-US", {
  currency: "USD",
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
  style: "currency",
});

export const CHART_FONT_FAMILY = "'Be Vietnam Pro', sans-serif";

// ECharts renders to canvas and cannot read CSS custom properties, so text
// colors are hardcoded per the Chart Color Specification dark-mode contrast
// rules: #9A9A9A (Gray 500) fails 4.5:1 on #2D2D2D — use #C4C4C4 minimum.
// Light-mode secondary text (ticks, axis titles) is #6C6C6C per spec v2.
const CHART_TEXT_PRIMARY_LIGHT = "#2d2d2d";
const CHART_TEXT_PRIMARY_DARK = "#f6f6f6";
const CHART_TEXT_SECONDARY_LIGHT = "#6c6c6c";
const CHART_TEXT_SECONDARY_DARK = "#c4c4c4";
// Legend labels use #6C6C6C in light mode (not primary #2d2d2d) and #C4C4C4 in
// dark mode per spec v2.
const CHART_TEXT_LEGEND_LIGHT = "#6c6c6c";
const CHART_TEXT_LEGEND_DARK = "#c4c4c4";

export const getChartTextColors = (isDark: boolean) => ({
  primary: isDark ? CHART_TEXT_PRIMARY_DARK : CHART_TEXT_PRIMARY_LIGHT,
  secondary: isDark ? CHART_TEXT_SECONDARY_DARK : CHART_TEXT_SECONDARY_LIGHT,
});

// Shared positioning/sizing for bottom-centered legends — spread this into
// every legend config, then add data and textStyle per chart. Swatches are
// 10px rounded squares and always flat, even for gradient-drawn series.
export const LEGEND_BASE = {
  bottom: 4,
  icon: "roundRect" as const,
  itemGap: 20,
  itemHeight: 10,
  itemWidth: 10,
  left: "center" as const,
} as const;

// Shared text style for chart legends — consistent font, weight, and
// theme-aware color across all chart types.
export const buildLegendTextStyle = (isDark: boolean) => ({
  color: isDark ? CHART_TEXT_LEGEND_DARK : CHART_TEXT_LEGEND_LIGHT,
  fontFamily: CHART_FONT_FAMILY,
  fontSize: 13,
  fontWeight: 500,
});

// Shared text style for chart axis tick labels and axis titles — 500 11px in
// the secondary color to keep axes visually subordinate to the data.
export const buildAxisLabelStyle = (isDark: boolean) => {
  const { secondary } = getChartTextColors(isDark);
  return {
    color: secondary,
    fontFamily: CHART_FONT_FAMILY,
    fontSize: 12,
    fontWeight: 500,
  };
};

const TOOLTIP_BG_LIGHT = "#ffffff";
const TOOLTIP_BG_DARK = "#2d2d33";
const TOOLTIP_BORDER_LIGHT = "#e4e4e4";
const TOOLTIP_BORDER_DARK = "#3a3a42";
const TOOLTIP_SHADOW_LIGHT = "0 4px 16px rgba(0, 0, 0, 0.1)";
const TOOLTIP_SHADOW_DARK = "0 4px 16px rgba(0, 0, 0, 0.4)";
const TOOLTIP_TEXT_LIGHT = "#2d2d2d";
const TOOLTIP_TEXT_DARK = "#f6f6f6";

// Creative, segment and browser names can be long, so tooltip text wraps at
// word boundaries inside a capped width instead of running off the chart. A
// single unbroken token ("D00405AJ_poss_3p_…") that is wider than the box is
// the only thing that breaks mid-word (overflow-wrap). `confine` keeps the box
// within the chart bounds.
const TOOLTIP_WRAP_CSS =
  "white-space: normal; overflow-wrap: break-word; max-width: 22rem;";

// Shared tooltip chrome per spec v2 — spread into every `tooltip` config, then
// add trigger/formatter per chart. Values inside the formatter's HTML should
// use `buildTooltipValueStyle`.
export const buildTooltipStyle = (isDark: boolean) => ({
  backgroundColor: isDark ? TOOLTIP_BG_DARK : TOOLTIP_BG_LIGHT,
  borderColor: isDark ? TOOLTIP_BORDER_DARK : TOOLTIP_BORDER_LIGHT,
  borderWidth: 1,
  confine: true,
  extraCssText: `border-radius: 10px; box-shadow: ${isDark ? TOOLTIP_SHADOW_DARK : TOOLTIP_SHADOW_LIGHT}; ${TOOLTIP_WRAP_CSS}`,
  padding: [10, 14],
  textStyle: {
    color: isDark ? TOOLTIP_TEXT_DARK : TOOLTIP_TEXT_LIGHT,
    fontFamily: CHART_FONT_FAMILY,
    fontSize: 12,
    fontWeight: 400,
  },
});

// Inline style for the emphasized value inside tooltip formatter HTML.
export const buildTooltipValueStyle = (isDark: boolean): string =>
  `font-weight: 700; color: ${isDark ? TOOLTIP_TEXT_DARK : TOOLTIP_TEXT_LIGHT};`;

// Every donut/ring on the dashboard uses the same pixel stroke width, whatever
// its diameter, so the rings read as one family (the Viewability ring is SVG,
// the channel and size donuts are ECharts — both draw this width).
export const DONUT_RING_WIDTH_PX = 18;
const DONUT_EDGE_GAP_PX = 4;

// Pixel radii for a donut drawn in a square box of `boxPx`: a fixed-width ring
// inset from the box edge.
export const donutRadius = (boxPx: number): [number, number] => {
  const outer = boxPx / 2 - DONUT_EDGE_GAP_PX;
  return [outer - DONUT_RING_WIDTH_PX, outer];
};
// Gap between donut slices, in degrees.
export const DONUT_SLICE_GAP_DEGREES = 2;

// Top corners only — bottom sits on the axis baseline.
export const BAR_BORDER_RADIUS_VERTICAL: number[] = [5, 5, 0, 0];

// Keeps axis labels inside the grid rect. Replaces `containLabel: true`, which
// ECharts 6 deprecated: it logs a dev-mode warning unless the
// LegacyGridContainLabel feature is registered. These two options are the
// documented equivalent.
export const CONTAIN_AXIS_LABELS = {
  outerBoundsContain: "axisLabel",
  outerBoundsMode: "same",
} as const;

// Shared impression axis formatter — abbreviated to keep tick labels legible
// at large volumes ("278K" not "278000"). Strips trailing ".0" on M-scale
// values for cleaner display ("1M" not "1.0M").
// Math.floor prevents rounding e.g. 999_999 up to "1000K" — the >= 1_000_000
// branch never fires for 999_999, so floor keeps it at "999K".
export const formatImpressionAxis = (value: number | string): string => {
  const num = Number(value);
  if (num >= 1_000_000)
    return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (num >= 1_000) return `${Math.floor(num / 1_000)}K`;
  return String(num);
};

// Grid line color for split-line rules inside chart areas, in both themes.
export const chartGridLineColor = (isDark: boolean): string =>
  isDark ? "#34343b" : "#f0f0f0";

// Two-decimal percentage figure (the caller adds the "%"), e.g. CTR "0.85".
export const PERCENT_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const SHARE_FORMATTER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
  style: "percent",
});

// Part-to-whole share (0–1) as a whole-number percent, for legends and donut centers.
export const formatShare = (share: number): string =>
  SHARE_FORMATTER.format(share);
